import React, { useState, useEffect } from "react";
import { Layout, Space } from "antd";
import TransactionsHome from "./transactionHome";
import { Stack, Box } from "@mui/material";
import { backendGetRequest, backendGetCached, backendUpdateCache, backendGetRequestWithCache } from "../../../helpers/callBackend";
import { documentTitle } from "../../../components/header/documentTitle";
import OuterBox from "../../../components/generic/arpariBoxes/OuterBox";


const PendingTransactions = () => {
    const [allAccounts, setAllAccounts] = useState([]);
    const [loadingAllAccounts, setLoadingAllAccounts] = useState(true);
    const [allAccountsUpdated, setAllAccountsUpdated] = useState(false);

    const [transactionDataRaw, setTransactionDataRaw] = useState([]);
    const [totalRecordsTransactions, setTotalRecordsTransactions] = useState(-1);
    const [transactionsLoading, setTransactionsLoading] = useState(true);

    const todayDate = new Date();
    let [currentMonth, setCurrentMonth] = useState(todayDate.getMonth() + 1);
    let [currentYear, setCurrentYear] = useState(todayDate.getFullYear());


    useEffect(() => {
        documentTitle();
    }, []);

    useEffect(() => {
        const processResponse = (data) => {
            if (!data) return;
            var bankAccounts = {};
            var i = 0, j = 0, k = 0, bankItem = {};
            for (i = 0; i < data.sub_portfolios.length; i++) {
                for (j = 0; j < data.sub_portfolios[i].bank_accounts.length; j++) {
                    bankItem = data.sub_portfolios[i].bank_accounts[j];
                    bankAccounts[bankItem['id']] = {
                        "bankName": bankItem['descriptor'],
                        "portfolioId": data.sub_portfolios[i]['id'],
                        "portfolioName": data.sub_portfolios[i]['descriptor'],
                        "propertyId": "",
                        "propertyName": "",
                        "accountNumberLast4": bankItem['additional_data']['account_number_last_4_attribute'],
                    }
                }
                for (j = 0; j < data.sub_portfolios[i].properties.length; j++) {
                    for (k = 0; k < data.sub_portfolios[i]['properties'][j]['bank_accounts'].length; k++) {
                        bankItem = data.sub_portfolios[i]['properties'][j]['bank_accounts'][k];
                        bankAccounts[bankItem['id']] = {
                            "bankName": bankItem['descriptor'],
                            "portfolioId": data.sub_portfolios[i]['id'],
                            "portfolioName": data.sub_portfolios[i]['descriptor'],
                            "propertyId": data.sub_portfolios[i]['properties'][j]['id'],
                            "propertyName": data.sub_portfolios[i]['properties'][j]['descriptor'],
                            "accountNumberLast4": bankItem['additional_data']['account_number_last_4_attribute'],
                        }
                    }
                }
            }
            for (i = 0; i < data.unlinked_properties.length; i++) {
                for (j = 0; j < data.unlinked_properties[i].bank_accounts.length; j++) {
                    bankItem = data.unlinked_properties[i].bank_accounts[j];
                    bankAccounts[bankItem['id']] = {
                        "bankName": bankItem['descriptor'],
                        "portfolioId": "",
                        "portfolioName": "",
                        "propertyId": data.unlinked_properties[i]['id'],
                        "propertyName": data.unlinked_properties[i]['descriptor'],
                        "accountNumberLast4": bankItem['additional_data']['account_number_last_4_attribute'],
                    }
                }
            }
            setAllAccounts(bankAccounts);
            setLoadingAllAccounts(false);
        }

        backendGetRequestWithCache("/api/portfolio/bank-accounts/", { params: { "sources": "UN;FI;PL;FT;MA" } },
            (cachedResponse) => {
                if (loadingAllAccounts && cachedResponse.data) {
                    processResponse(cachedResponse.data);
                }
            },
            (nonCachedResponse) => {
                if (nonCachedResponse.data) {
                    processResponse(nonCachedResponse.data);
                }
            },
        );
    }, []);

    const onLoadAllTransactions = ({ month: currentMonth, year: currentYear }) => {
        const fetchAllInitialTransactions = async (bankAccountId) => {
            await onLoadTransactions({ bankAccountId: bankAccountId, month: currentMonth, year: currentYear });
        }
        for (let bankAccountId of Object.keys(allAccounts)) {
            fetchAllInitialTransactions(bankAccountId);
        }
    }

    useEffect(() => {
        if (allAccountsUpdated) {
            return;
        }
        if (allAccounts.length) {
            setAllAccountsUpdated(true);
        }
        onLoadAllTransactions({ month: currentMonth, year: currentYear });
    }, [allAccounts]);

    const decrementMonth = (month, year, decrementInplace = false) => {
        if (month === 1) {
            month = 12;
            year -= 1;
        } else {
            month -= 1;
        }
        if (decrementInplace) {
            setCurrentMonth(month);
            setCurrentYear(year);
        }
        return { month: month, year: year };
    }

    const fetchTransactionPages = async ({ bankAccountId, month, year, isStored, setTransactionDataRaw, backendGetCached, backendGetRequest, backendUpdateCache }) => {
        const allTransactionsURL = "/api/transactions/all";
        const res = await backendGetRequest(allTransactionsURL, {
            params: { 'page_index': 0, bank_account_id: bankAccountId, month: month, year: year, pending: true, stored: isStored }
        });
        setTotalRecordsTransactions(res?.data?.total_records);
        const fetchPages = async (isCached) => {
            for (let i = 1; i <= res?.data?.total_pages; i++) {
                try {
                    const pageResponse = isCached
                        ? await backendGetCached(allTransactionsURL, {
                            params: { 'page_index': i, bank_account_id: bankAccountId, month: month, year: year, pending: true, stored: isStored }
                        })
                        : await backendGetRequest(allTransactionsURL, {
                            params: { 'page_index': i, bank_account_id: bankAccountId, month: month, year: year, pending: true, stored: isStored }
                        });
                    if (pageResponse.data) {
                        setTransactionDataRaw(prevState => {
                            let newTransactions = isCached
                                ? pageResponse.data?.all_transactions?.filter(transaction =>
                                    !prevState.some(txn => txn.id === transaction.id)
                                )
                                : [...prevState];
                            if (!isCached) {
                                pageResponse.data?.all_transactions?.forEach(transaction => {
                                    const existingTransactionIndex = newTransactions.findIndex(txn => txn.id === transaction.id);
                                    if (existingTransactionIndex !== -1) {
                                        newTransactions[existingTransactionIndex] = transaction;
                                    } else {
                                        newTransactions.push(transaction);
                                    }
                                });
                                backendUpdateCache(allTransactionsURL, {
                                    params: { 'page_index': i, bank_account_id: bankAccountId, month: month, year: year, pending: true, stored: isStored }
                                }, pageResponse.data);
                            }
                            return isCached ? [...prevState, ...newTransactions] : newTransactions;
                        });
                    }
                } catch (error) {
                    console.error(`Error fetching ${isCached ? 'cached' : 'non-cached'} page ${i}:`, error);
                }
            }
        };
        await Promise.all([fetchPages(true), fetchPages(false)]);
    };

    const onLoadTransactions = async ({ bankAccountId, month, year }) => {
        try {
            setTransactionsLoading(true);
            decrementMonth(month, year, true);
            await Promise.all([
                fetchTransactionPages({
                    bankAccountId,
                    month,
                    year,
                    isStored: false, // for normal transactions (from source)
                    setTransactionDataRaw,
                    backendGetCached,
                    backendGetRequest,
                    backendUpdateCache
                }),
                fetchTransactionPages({
                    bankAccountId,
                    month,
                    year,
                    isStored: true, // for stored transactions (from db)
                    setTransactionDataRaw,
                    backendGetCached,
                    backendGetRequest,
                    backendUpdateCache
                })
            ]);
        } catch (error) {
            console.error('Error fetching all transactions:', error);
        } finally {
            setTransactionsLoading(false);
        }
    };

    return (
        <OuterBox>
            <TransactionsHome
                pending={true}
                transactionData={transactionDataRaw}
                recordsCount={totalRecordsTransactions}
                loadingTable={transactionsLoading}
                onLoadTransactions={onLoadAllTransactions}
                transactionsLoading={transactionsLoading}
                currentMonth={currentMonth}
                currentYear={currentYear}
            />
        </OuterBox>
    );
}

export default PendingTransactions;